import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableRow, TableCell, Typography, Paper, Divider, Grid, TextField, Button, IconButton, Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress, Tooltip, Zoom, Fade, Backdrop, Snackbar, Alert, Checkbox, FormGroup, FormControlLabel, FormControl, RadioGroup, FormControlLabel as MuiFormControlLabel, Radio, MenuItem } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import PaidIcon from '@mui/icons-material/Paid';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import StarIcon from '@mui/icons-material/Star';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import LanIcon from '@mui/icons-material/Lan';
import PreviewIcon from '@mui/icons-material/Preview';
import SchoolIcon from '@mui/icons-material/School';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import HouseIcon from '@mui/icons-material/House';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { motion } from 'framer-motion';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import UpdateIcon from '@mui/icons-material/Update';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkIcon from '@mui/icons-material/Work';
import LoadingScreen from '../../components/LoadingScreen';
import { accentBorderCard, designTokens } from '../../theme/designTokens';
import { useTheme } from '@mui/material/styles';
import { bg, text as textColors, border } from '../../utils/darkModeAdapter';

/**
 * RequirementsView:
 *  - `requirements`: ユーザー入力または取得した採用要件
 *  - `adv`: この画面向けのAIアドバイスオブジェクト (APIから取得)
 */
export default function RequirementsView({ requirements, adv = {}, onRequirementsChange }) {
  const theme = useTheme();
  // 編集モード管理
  const [isEditing, setIsEditing] = useState(false);
  const [editedRequirements, setEditedRequirements] = useState({ ...requirements });
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [inputTimeoutId, setInputTimeoutId] = useState(null);
  const [localEditedRequirements, setLocalEditedRequirements] = useState({ ...requirements });

  // 編集開始
  const handleStartEdit = () => {
    // 編集モード開始時に、予算は生の数値を使用
    setEditedRequirements({ ...requirements });
    setLocalEditedRequirements({ ...requirements });
    
    setIsEditing(true);
  };

  // 編集キャンセル
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // 項目の変更（デバウンス処理を追加）
  const handleFieldChange = (field, value) => {
    // 予算フィールドの場合は即時更新
    if (field === 'budget') {
      // ローカルの状態とメイン状態の両方を即時更新（UIに反映）
      setLocalEditedRequirements(prev => ({
        ...prev,
        [field]: value
      }));
      
      // 予算フィールドはタイマーなしで即時更新
      setEditedRequirements(prev => ({
        ...prev,
        [field]: value
      }));
      
      // タイマーは使用しない
      if (inputTimeoutId) {
        clearTimeout(inputTimeoutId);
        setInputTimeoutId(null);
      }
    } else {
      // 予算以外のフィールドは従来通りの処理
      // ローカルの状態は即時更新（UIに反映）
      setLocalEditedRequirements(prev => ({
        ...prev,
        [field]: value
      }));
      
      // 既存のタイマーがあればクリア
      if (inputTimeoutId) {
        clearTimeout(inputTimeoutId);
      }
      
      // 新しいタイマーをセット（500ms後に実際の状態を更新）
      const newTimeoutId = setTimeout(() => {
        setEditedRequirements(prev => ({
          ...prev,
          [field]: value
        }));
      }, 500);
      
      setInputTimeoutId(newTimeoutId);
    }
  };

  // ダイアログ内でのフィールド変更（ダイアログ専用）
  const handleDialogFieldChange = (e) => {
    if (!selectedField) return;
    
    const newValue = e.target.value;
    
    // ローカルの状態は即時更新（UIに反映）
    setLocalEditedRequirements(prev => ({
      ...prev,
      [selectedField]: newValue
    }));
  };

  // コンポーネントがアンマウントされる際にタイマーをクリア
  useEffect(() => {
    return () => {
      if (inputTimeoutId) {
        clearTimeout(inputTimeoutId);
      }
    };
  }, [inputTimeoutId]);

  // 要件が更新されたらエディタの状態も更新（編集中でない場合のみ）
  useEffect(() => {
    // 編集中でない場合のみ状態を更新
    if (!isEditing) {
      setEditedRequirements({ ...requirements });
      setLocalEditedRequirements({ ...requirements });
    }
  }, [requirements, isEditing]);

  // 編集内容を保存して更新
  const handleSaveChanges = () => {
    if (onRequirementsChange) {
      // タイマーがあればクリアして最新の編集内容を確実に反映
      if (inputTimeoutId) {
        clearTimeout(inputTimeoutId);
        setInputTimeoutId(null);
      }
      
      // 最新のローカル編集内容を使用
      const finalRequirements = { ...localEditedRequirements };
      
      // recruitTypeが未設定の場合、デフォルト値を設定
      if (!finalRequirements.recruitType) {
        finalRequirements.recruitType = 'both'; // デフォルト値を「両方」に設定
      }
      
      // ローディング状態を設定
      setIsRegenerating(true);
      setError(null);
      
      onRequirementsChange(finalRequirements)
        .then(() => {
          // 成功時のみ編集モードを終了
          setIsEditing(false);
          setSnackbarOpen(true);
        })
        .catch((err) => {
          console.error('Error updating requirements:', err);
          // エラー時は編集モードを維持し、ユーザーの入力を保持
          if (err.response && err.response.status === 401) {
            setError('認証エラーが発生しました。再ログインが必要です。');
          } else {
            setError('更新中にエラーが発生しました。もう一度お試しください。');
          }
          setSnackbarOpen(true);
        })
        .finally(() => {
          setIsRegenerating(false);
        });
    } else {
      setIsEditing(false);
    }
  };

  // ダイアログでの編集確定
  const handleDialogConfirm = () => {
    if (selectedField && onRequirementsChange) {
      // タイマーがあればクリアして最新の編集内容を確実に反映
      if (inputTimeoutId) {
        clearTimeout(inputTimeoutId);
        setInputTimeoutId(null);
      }
      
      // 更新する要件データを作成
      const updatedReq = {
        ...requirements,
        [selectedField]: localEditedRequirements[selectedField]
      };
      
      // recruitTypeが未設定の場合、デフォルト値を設定
      if (!updatedReq.recruitType && !selectedField.includes('recruitType')) {
        updatedReq.recruitType = 'both'; // デフォルト値を「両方」に設定
      }
      
      // ローディング状態を設定
      setIsRegenerating(true);
      setError(null);
      
      onRequirementsChange(updatedReq)
        .then(() => {
          // 成功時のみダイアログを閉じる
          setDialogOpen(false);
          setSnackbarOpen(true);
        })
        .catch((err) => {
          console.error('Error updating requirements:', err);
          // エラー時はダイアログを維持し、ユーザーの入力を保持
          if (err.response && err.response.status === 401) {
            setError('認証エラーが発生しました。再ログインが必要です。');
          } else {
            setError('更新中にエラーが発生しました。もう一度お試しください。');
          }
          setSnackbarOpen(true);
        })
        .finally(() => {
          setIsRegenerating(false);
        });
    } else {
      setDialogOpen(false);
    }
  };

  // 特定のフィールドだけを編集して再生成
  const handleQuickEdit = (field) => {
    // 予算フィールドの場合、表示用文字列ではなく生の数値を使用
    if (field === 'budget') {
      setLocalEditedRequirements({
        ...requirements
      });
      setEditedRequirements({
        ...requirements
      });
    } else {
      setLocalEditedRequirements({ ...requirements });
      setEditedRequirements({ ...requirements });
    }
    setSelectedField(field);
    setDialogOpen(true);
  };

  // Snackbarを閉じる
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // フィールド名を日本語表示名に変換する関数
  const getFieldDisplayName = (fieldName) => {
    const fieldNameMap = {
      'startDate': '採用開始日',
      'endDate': '採用終了日',
      'targetHires': '希望人数',
      'budget': '採用予算',
      'position': '募集職種',
      'desiredSkillLevel': '想定スキルレベル',
      'orgIssues': '採用背景',
      'recruitType': '採用タイプ',
      'industry': '業界',
      'companySize': '企業規模',
      'existingOrgStructure': '組織体制',
      'existingTalentSkill': '既存人材スキル',
      'pastRecruitData': '過去の採用データ',
      'employmentType': '雇用形態',
      'workStyle': '働き方',
      'location': '勤務地',
      'prioritySchedule': '採用優先スケジュール'
    };
    return fieldNameMap[fieldName] || fieldName;
  };

  // 例: "50000" => "5万円" を返す関数
  function formatBudget(value) {
    if (!value) {
      return "未設定";
    }
    // 数値に変換
    const intVal = parseInt(value, 10);
    if (isNaN(intVal)) {
      // 数値に変換できなかった場合はそのまま表示するか、"未設定"にする
      return "未設定";
    }
    // 10000円単位で割り算して整数部分を取得 => "○万円"
    const man = Math.floor(intVal / 10000);
    return `${man}万円`;
  }

  // -------------------------------------------------------------
  // カードコンポーネント
  // -------------------------------------------------------------
  function CardItem({ title, advice, children }) {
    const featureTheme = designTokens.featureThemes.recruit_plan;

    return (
      <Paper
        component={motion.div}
        elevation={0}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, bounce: 0.3 }}
        sx={{
          ...accentBorderCard('recruit_plan', theme),
          p: 2.5,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 0.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: featureTheme.primary,
            }}
          >
            {title}
          </Typography>
          {advice && <Box>{advice}</Box>}
        </Box>
        <Box sx={{ mt: 1.5, pl: 0.5 }}>
          {children}
        </Box>
      </Paper>
    );
  }
  
  // adv が空文字なら fallback する関数
  const fallback = (text, defaultText) => (text && text.trim() !== '' ? text : defaultText);
  
  // recruitType を「新卒」「中途」「両方」などに変換するヘルパー
  const getRecruitTypeLabel = (recruitType) => {
    if (!recruitType) return '未設定';
    if (recruitType === 'both') return '新卒・中途';
    if (recruitType === 'newGrad') return '新卒';
    if (recruitType === 'midCareer') return '中途';
    return '未設定';
  };

  // EditableCell コンポーネント（モダンなGrid/Flexレイアウト）
  const EditableCell = ({ icon, label, value, field, isEditing, onEditingChange, onChange }) => {
    // ローカルの値を管理
    // 予算の場合は生の数値を使用する
    const [localValue, setLocalValue] = useState(
      field === 'budget' ? (requirements.budget || '') : (value || '')
    );

    // 親コンポーネントの値が変わったら更新
    useEffect(() => {
      if (field === 'budget') {
        setLocalValue(requirements.budget || '');
      } else {
        setLocalValue(value || '');
      }
    }, [value, field, requirements.budget]);

    // ローカル値の変更ハンドラ - 即時反映するが親へは通知しない
    const handleLocalChange = (e) => {
      // 数値フィールドの場合は数値のみ許可
      if (field === 'targetHires' || field === 'budget') {
        const newValue = e.target.value.replace(/[^0-9]/g, '');
        setLocalValue(newValue);
      } else {
        setLocalValue(e.target.value);
      }
    };

    // 入力完了時に親コンポーネントへ変更を通知
    const handleCommit = () => {
      onChange(field, localValue);
    };

    // Enterキーで確定処理
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        handleCommit();
        onEditingChange(false);
      }
    };

    return (
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        sx={{
          display: 'flex',
          gap: 2.5,
          p: 2.5,
          mb: 2,
          borderRadius: 2,
          backgroundColor: isEditing ? 'rgba(33, 150, 243, 0.04)' : bg(theme).card,
          border: `1px solid ${isEditing ? 'rgba(33, 150, 243, 0.2)' : border(theme).default}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: isEditing ? 'rgba(33, 150, 243, 0.06)' : bg(theme).subtle,
            borderColor: isEditing ? 'rgba(33, 150, 243, 0.3)' : border(theme).light,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }
        }}
      >
        {/* アイコンエリア */}
        <Box
          sx={{
            flexShrink: 0,
            width: 48,
            height: 48,
            borderRadius: 2,
            backgroundColor: `${designTokens.featureThemes.recruit_plan.primary}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s',
          }}
        >
          {React.cloneElement(icon, {
            sx: {
              fontSize: 28,
              color: designTokens.featureThemes.recruit_plan.primary,
            }
          })}
        </Box>

        {/* コンテンツエリア */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* ラベルと編集ボタン */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              {label}
            </Typography>
            {!isEditing && (
              <Tooltip
                title={`${label}を編集する`}
                placement="top"
                arrow
                TransitionComponent={Zoom}
              >
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleQuickEdit(field)}
                  sx={{
                    ml: 1,
                    color: designTokens.featureThemes.recruit_plan.primary,
                    '&:hover': {
                      backgroundColor: `${designTokens.featureThemes.recruit_plan.primary}15`,
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  <EditNoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* 値表示または編集フィールド */}
          {isEditing ? (
            field === 'recruitType' ? (
              <Box sx={{ mt: 2 }}>
                <FormGroup row sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        icon={<CheckCircleOutlineIcon />}
                        checkedIcon={<CheckCircleIcon />}
                        checked={localValue === 'newGrad' || localValue === 'both'}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const isMidCareerChecked = localValue === 'midCareer' || localValue === 'both';
                          
                          let newType = 'both';
                          if (isChecked && !isMidCareerChecked) newType = 'newGrad';
                          else if (!isChecked && isMidCareerChecked) newType = 'midCareer';
                          else if (!isChecked && !isMidCareerChecked) newType = '';
                          
                          // ローカルの状態は即時更新
                          setLocalValue(newType);
                        }}
                        name="newGradChecked"
                        sx={{
                          color: '#1E88E5',
                          '&.Mui-checked': {
                            color: '#1E88E5',
                          },
                        }}
                      />
                    }
                    label="新卒採用"
                    sx={{
                      '.MuiFormControlLabel-label': {
                        fontWeight: 'bold',
                        color: '#0D47A1',
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        icon={<CheckCircleOutlineIcon />}
                        checkedIcon={<CheckCircleIcon />}
                        checked={localValue === 'midCareer' || localValue === 'both'}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const isNewGradChecked = localValue === 'newGrad' || localValue === 'both';
                          
                          let newType = 'both';
                          if (isChecked && !isNewGradChecked) newType = 'midCareer';
                          else if (!isChecked && isNewGradChecked) newType = 'newGrad';
                          else if (!isChecked && !isNewGradChecked) newType = '';
                          
                          // ローカルの状態は即時更新
                          setLocalValue(newType);
                        }}
                        name="midCareerChecked"
                        sx={{
                          color: '#FB8C00',
                          '&.Mui-checked': {
                            color: '#FB8C00',
                          },
                        }}
                      />
                    }
                    label="中途採用"
                    sx={{
                      '.MuiFormControlLabel-label': {
                        fontWeight: 'bold',
                        color: '#E65100',
                      },
                    }}
                  />
                </FormGroup>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  ※ 新卒・中途いずれか、あるいは両方を選択してください
                </Typography>
              </Box>
            ) : field === 'startDate' || field === 'endDate' ? (
              <TextField
                fullWidth
                size="small"
                type="date"
                label={field === 'startDate' ? '採用開始日' : '採用終了日'}
                value={localValue}
                onChange={handleLocalChange}
                onBlur={() => {
                  handleCommit();
                  onEditingChange(false);
                }}
                onKeyDown={handleKeyDown}
                margin="dense"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                helperText={field === 'endDate' ? '※ 採用開始日以降の日付を指定してください。' : null}
                sx={{
                  mt: 0.5,
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#4e7df3',
                    },
                  },
                }}
              />
            ) : field === 'prioritySchedule' ? (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={1}>
                  {['Q1', 'Q2', 'Q3', 'Q4'].map((quarter, index) => (
                    <Grid item xs={3} key={quarter}>
                      <Box 
                        onClick={() => {
                          setLocalValue(quarter);
                        }}
                        sx={{ 
                          border: '1px solid',
                          borderColor: localValue === quarter ? '#4e7df3' : border(theme).default,
                          borderRadius: 2,
                          p: 1,
                          textAlign: 'center',
                          cursor: 'pointer',
                          backgroundColor: localValue === quarter ? 'rgba(78, 125, 243, 0.08)' : 'transparent',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: '#4e7df3',
                            backgroundColor: 'rgba(78, 125, 243, 0.04)',
                          }
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 'bold',
                            color: localValue === quarter ? '#4e7df3' : 'text.secondary',
                          }}
                        >
                          {quarter}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: 'text.secondary',
                          }}
                        >
                          {['1-3月', '4-6月', '7-9月', '10-12月'][index]}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="例: 4月, 5月頃, 2024年1月"
                  value={!['Q1', 'Q2', 'Q3', 'Q4'].includes(localValue) ? localValue : ''}
                  onChange={handleLocalChange}
                  onBlur={() => {
                    handleCommit();
                    onEditingChange(false);
                  }}
                  onKeyDown={handleKeyDown}
                  margin="dense"
                  variant="outlined"
                  sx={{ 
                    mt: 1,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#4e7df3',
                      },
                    },
                  }}
                />
              </Box>
            ) : field === 'targetHires' || field === 'budget' ? (
              <TextField
                fullWidth
                size="small"
                type="text"
                label={field === 'targetHires' ? '希望人数' : '採用予算'}
                value={localValue}
                onChange={handleLocalChange}
                onBlur={() => {
                  handleCommit();
                  onEditingChange(false);
                }}
                onKeyDown={handleKeyDown}
                margin="dense"
                variant="outlined"
                helperText={field === 'budget' ? '※ 半角数字のみ入力（円単位）' : '※ 半角数字のみ入力'}
                InputProps={{
                  inputProps: { min: 1 }
                }}
                sx={{
                  mt: 0.5,
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#4e7df3',
                    },
                  },
                }}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                value={localValue}
                onChange={handleLocalChange}
                onBlur={() => {
                  handleCommit();
                  onEditingChange(false);
                }}
                onKeyDown={handleKeyDown}
                margin="dense"
                variant="outlined"
                multiline={field === 'orgIssues' || field === 'existingTalentSkill' || field === 'pastRecruitData'}
                rows={field === 'orgIssues' || field === 'existingTalentSkill' || field === 'pastRecruitData' ? 4 : 1}
                sx={{
                  mt: 0.5,
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#4e7df3',
                    },
                  },
                }}
              />
            )
          ) : (
            <Typography
              variant="body1"
              sx={{
                color: value ? 'text.primary' : 'text.disabled',
                fontSize: '0.9375rem',
                fontWeight: value ? 500 : 400,
                fontStyle: value ? 'normal' : 'italic',
                lineHeight: 1.6,
              }}
            >
              {value || '未設定'}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  // EditableCell コンポーネントに渡すプロパティを修正
  const renderEditableCell = (icon, label, value, field) => {
    // 予算フィールドの場合は特別な処理
    if (field === 'budget') {
      return (
        <EditableCell 
          icon={icon}
          label={label}
          value={value} // 表示用の値（formatBudget済み）
          field={field}
          isEditing={isEditing}
          onEditingChange={(editing) => {
            if (editing === false && field === 'budget') {
              // 予算フィールドの編集終了時は即時更新
              setEditedRequirements(localEditedRequirements);
            } else if (editing === false) {
              // 編集終了時にタイマーを設定して実際の状態を更新
              if (inputTimeoutId) {
                clearTimeout(inputTimeoutId);
              }
              
              const newTimeoutId = setTimeout(() => {
                setEditedRequirements(localEditedRequirements);
              }, 300);
              
              setInputTimeoutId(newTimeoutId);
            }
          }}
          onChange={handleFieldChange}
        />
      );
    }
    
    // 予算以外のフィールドは従来通り
    return (
      <EditableCell 
        icon={icon}
        label={label}
        value={value}
        field={field}
        isEditing={isEditing}
        onEditingChange={(editing) => {
          if (editing === false) {
            // 編集終了時にタイマーを設定して実際の状態を更新
            if (inputTimeoutId) {
              clearTimeout(inputTimeoutId);
            }
            
            const newTimeoutId = setTimeout(() => {
              setEditedRequirements(localEditedRequirements);
            }, 300);
            
            setInputTimeoutId(newTimeoutId);
          }
        }}
        onChange={handleFieldChange}
      />
    );
  };

  // isDialogInvalidを修正してエラーメッセージも返すようにする
  const isDialogInvalid = () => {
    // ダイアログが開いていない場合は無効としない
    if (!dialogOpen || !selectedField) return false;
    
    // localEditedRequirementsを使用して判定
    const value = localEditedRequirements[selectedField];
    
    // 必須項目の場合、空白は無効
    const requiredFields = ['position', 'targetHires', 'budget', 'startDate', 'endDate', 'desiredSkillLevel', 'orgIssues'];
    if (requiredFields.includes(selectedField)) {
      if (!value || value.trim() === '') {
        return true;
      }
    }
    
    // 採用タイプの特別チェック
    if (selectedField === 'recruitType' && (!value || value === '')) {
      return true;
    }
    
    // 日付の検証
    if (selectedField === 'endDate' && localEditedRequirements.startDate) {
      const startDate = new Date(localEditedRequirements.startDate);
      const endDate = new Date(value);
      if (endDate < startDate) {
        return true;
      }
    }
    
    // 数値フィールドの検証
    if (selectedField === 'targetHires' || selectedField === 'budget') {
      const numValue = Number(value);
      if (isNaN(numValue) || numValue <= 0) {
        return true;
      }
    }
    
    return false;
  };

  // isFormInvalidを修正してlocalEditedRequirementsを使用する
  const isFormInvalid = () => {
    // 必須項目のバリデーション
    const requiredFields = ['position', 'targetHires', 'budget', 'startDate', 'endDate', 'desiredSkillLevel', 'orgIssues'];
    for (const field of requiredFields) {
      const value = localEditedRequirements[field];
      if (!value || value.trim() === '') {
        return true;
      }
    }
    
    // 採用タイプの特別ケース
    if (!localEditedRequirements.recruitType) {
      return true;
    }
    
    // 日付の検証: endDate が startDate 以降であることを確認
    if (localEditedRequirements.startDate && localEditedRequirements.endDate) {
      const start = new Date(localEditedRequirements.startDate);
      const end = new Date(localEditedRequirements.endDate);
      if (end < start) {
        return true;
      }
    }
    
    // 数値フィールドの検証
    for (const field of ['targetHires', 'budget']) {
      const numValue = Number(localEditedRequirements[field]);
      if (isNaN(numValue) || numValue <= 0) {
        return true;
      }
    }
    
    return false;
  };

  // バリデーションエラーメッセージを取得する関数を追加
  const getValidationErrorMessage = () => {
    // 必須項目のバリデーション
    const requiredFields = ['position', 'targetHires', 'budget', 'startDate', 'endDate', 'desiredSkillLevel', 'orgIssues'];
    for (const field of requiredFields) {
      const value = localEditedRequirements[field];
      if (!value || value.trim() === '') {
        return `${getFieldDisplayName(field)}は必須項目です`;
      }
    }
    
    // 採用タイプの特別ケース
    if (!localEditedRequirements.recruitType) {
      return '採用タイプを選択してください（新卒または中途）';
    }
    
    // 日付の検証: endDate が startDate 以降であることを確認
    if (localEditedRequirements.startDate && localEditedRequirements.endDate) {
      const start = new Date(localEditedRequirements.startDate);
      const end = new Date(localEditedRequirements.endDate);
      if (end < start) {
        return '採用終了日は採用開始日以降に設定してください';
      }
    }
    
    // 数値フィールドの検証
    for (const field of ['targetHires', 'budget']) {
      const numValue = Number(localEditedRequirements[field]);
      if (isNaN(numValue) || numValue <= 0) {
        return `${getFieldDisplayName(field)}は1以上の数値を入力してください`;
      }
    }
    
    return '';
  };

  // ダイアログのバリデーションエラーメッセージを取得する関数
  const getDialogValidationErrorMessage = () => {
    if (!dialogOpen || !selectedField) return '';
    
    const value = localEditedRequirements[selectedField];
    
    // 必須項目の場合、空白は無効
    const requiredFields = ['position', 'targetHires', 'budget', 'startDate', 'endDate', 'desiredSkillLevel', 'orgIssues'];
    if (requiredFields.includes(selectedField)) {
      if (!value || value.trim() === '') {
        return `${getFieldDisplayName(selectedField)}は必須項目です`;
      }
    }
    
    // 採用タイプの特別チェック
    if (selectedField === 'recruitType' && (!value || value === '')) {
      return '採用タイプを選択してください（新卒または中途）';
    }
    
    // 日付の検証
    if (selectedField === 'endDate' && localEditedRequirements.startDate) {
      const startDate = new Date(localEditedRequirements.startDate);
      const endDate = new Date(value);
      if (endDate < startDate) {
        return '採用終了日は採用開始日以降に設定してください';
      }
    }
    
    // 数値フィールドの検証
    if (selectedField === 'targetHires' || selectedField === 'budget') {
      const numValue = Number(value);
      if (isNaN(numValue) || numValue <= 0) {
        return `${getFieldDisplayName(selectedField)}は1以上の数値を入力してください`;
      }
    }
    
    return '';
  };

  // ダイアログを閉じる処理
  const handleCloseDialog = () => {
    // タイマーがあればクリア
    if (inputTimeoutId) {
      clearTimeout(inputTimeoutId);
      setInputTimeoutId(null);
    }
    
    setDialogOpen(false);
  };

  // ダイアログに表示するテキストフィールドのオンチェンジハンドラー
  const handleDialogTextChange = (e) => {
    if (!selectedField) return;
    
    let newValue = e.target.value;
    
    // 数値フィールドの処理
    if (selectedField === 'targetHires' || selectedField === 'budget') {
      newValue = newValue.replace(/[^0-9]/g, '');
    }
    
    setLocalEditedRequirements(prev => ({
      ...prev,
      [selectedField]: newValue
    }));
  };

  return (
    <Box sx={{ my: 'auto', mx: 'auto', position: 'relative' }}>
      {/* ローディングオーバーレイ */}
      {isRegenerating && (
        <LoadingScreen
          variant="overlay"
          message="採用計画を再生成中..."
          description={`${selectedField ? `${getFieldDisplayName(selectedField)}を更新しています。` : ''}最適な計画を作成するために、AIが分析しています。少々お待ちください。`}
          featureKey="recruit_plan"
          loadingType="recruit_plan"
        />
      )}

      {/* エラー通知 */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? "error" : "success"} 
          sx={{ width: '100%' }}
        >
          {error ? error : "採用要件を更新しました"}
        </Alert>
      </Snackbar>

      {/* グローバル編集ボタン */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'flex-end',
        mb: 3,
        position: 'sticky',
        top: 16,
        zIndex: 10
      }}>
        {isRegenerating ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#4e7df3' }}>
              採用計画再生成中...
            </Typography>
            <CircularProgress size={24} sx={{ color: '#4e7df3' }} />
          </Box>
        ) : isEditing ? (
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            style={{ width: '100%' }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
              {/* バリデーションエラーメッセージ */}
              {isFormInvalid() && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'error.main', 
                    fontWeight: 'medium', 
                    mb: 1,
                    p: 1,
                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                    borderRadius: 1,
                    width: '100%',
                    textAlign: 'center'
                  }}
                >
                  {getValidationErrorMessage()}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  startIcon={<CancelIcon />}
                  size="medium"
                  variant="outlined"
                  color="inherit"
                  onClick={handleCancelEdit}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    borderColor: border(theme).default,
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: border(theme).light,
                      backgroundColor: bg(theme).subtle
                    }
                  }}
                >
                  キャンセル
                </Button>
                <Button
                  startIcon={<SaveIcon />}
                  size="medium"
                  variant="contained"
                  color="primary"
                  onClick={handleSaveChanges}
                  disabled={isFormInvalid()}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                    backgroundColor: '#4e7df3',
                    fontWeight: 600,
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      backgroundColor: '#4065d0',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: border(theme).default,
                      color: 'text.disabled',
                      boxShadow: 'none',
                    }
                  }}
                >
                  保存して再生成
                </Button>
              </Box>
            </Box>
          </motion.div>
        ) : (
          <Tooltip 
            title="全ての採用要件を編集して再生成します" 
            placement="left" 
            arrow
          >
            <Button
              startIcon={<ModeEditIcon />}
              size="medium"
              variant="contained"
              color="primary"
              onClick={handleStartEdit}
              sx={{
                borderRadius: 2,
                px: 3,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                backgroundColor: designTokens.featureThemes.recruit_plan.primary,
                fontWeight: 600,
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  backgroundColor: '#d85f28',
                }
              }}
            >
              全て編集
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* (1) 採用タイプ */}
      <CardItem
        title="採用タイプ"
        adviceContent={fallback(adv.recruitScale, '')}
      >
        <Box>
          {renderEditableCell(
            <BusinessCenterIcon />,
            '採用タイプ',
            getRecruitTypeLabel(requirements.recruitType),
            'recruitType'
          )}
          {isEditing && !localEditedRequirements.recruitType && (
            <Box sx={{ pl: 8, pb: 1 }}>
              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'medium' }}>
                採用タイプを選択してください（新卒または中途）
              </Typography>
            </Box>
          )}
        </Box>
      </CardItem>

      {/* (2) 採用スケジュール */}
      <CardItem
        title="採用スケジュール"
        adviceContent={fallback(adv.schedule, '')}
      >
        <Box>
          {renderEditableCell(
            <EventIcon />,
            '採用開始日',
            requirements.startDate,
            'startDate'
          )}
          {renderEditableCell(
            <EventIcon />,
            '採用終了日',
            requirements.endDate,
            'endDate'
          )}
        </Box>
      </CardItem>

      {/* (3) 募集規模 */}
      <CardItem
        title="募集規模"
        adviceContent={fallback(adv.recruitScale, '')}
      >
        <Box>
          {renderEditableCell(
            <PeopleIcon />,
            '希望人数',
            requirements.targetHires,
            'targetHires'
          )}
          {renderEditableCell(
            <PaidIcon />,
            '採用予算',
            formatBudget(requirements.budget),
            'budget'
          )}
        </Box>
      </CardItem>

      {/* (4) 募集職種・スキル */}
      <CardItem
        title="募集職種・スキル"
        adviceContent={fallback(adv.positionAndSkill, '')}
      >
        <Box>
          {renderEditableCell(
            <AssignmentIndIcon />,
            '募集職種',
            requirements.position,
            'position'
          )}
          {renderEditableCell(
            <StarIcon />,
            '想定スキルレベル',
            requirements.desiredSkillLevel,
            'desiredSkillLevel'
          )}
          {renderEditableCell(
            <CorporateFareIcon />,
            '業界',
            requirements.industry,
            'industry'
          )}
        </Box>
      </CardItem>

      {/* (5) 組織の課題・採用背景 */}
      <CardItem
        title="組織の課題・採用背景"
        adviceContent={fallback(adv.orgIssues, '')}
      >
        <Box>
          {renderEditableCell(
            <CorporateFareIcon />,
            '採用背景',
            requirements.orgIssues,
            'orgIssues'
          )}
        </Box>
      </CardItem>

      {/* (6) 任意入力項目 */}
      <CardItem
        title="その他企業情報・条件"
        adviceContent={fallback(adv.optionalInfo, '')}
      >
        <Box>
          {/* existingTalentSkill */}
          {renderEditableCell(
            <SchoolIcon />,
            '既存人材スキル',
            requirements.existingTalentSkill,
            'existingTalentSkill'
          )}
          {/* pastRecruitData */}
          {renderEditableCell(
            <PreviewIcon />,
            '過去の採用データ',
            requirements.pastRecruitData,
            'pastRecruitData'
          )}
          {/* employmentType */}
          {renderEditableCell(
            <WorkHistoryIcon />,
            '雇用形態',
            requirements.employmentType,
            'employmentType'
          )}
          {/* workStyle */}
          {renderEditableCell(
            <WorkOutlineIcon />,
            '働き方',
            requirements.workStyle,
            'workStyle'
          )}
          {/* prioritySchedule */}
          {renderEditableCell(
            <ScheduleIcon />,
            '採用優先スケジュール',
            requirements.prioritySchedule,
            'prioritySchedule'
          )}
        </Box>
      </CardItem>

      {/* 特定フィールド編集ダイアログ */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        TransitionComponent={Fade}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          pt: 2.5,
          borderBottom: `1px solid ${border(theme).light}`,
          backgroundColor: bg(theme).subtle,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          {selectedField && {
            'position': <WorkIcon color="primary" />,
            'targetHires': <PeopleIcon color="primary" />,
            'budget': <PaidIcon color="primary" />,
            'startDate': <EventIcon color="primary" />,
            'endDate': <EventIcon color="primary" />,
            'desiredSkillLevel': <StarIcon color="primary" />,
            'industry': <BusinessCenterIcon color="primary" />,
            'recruitType': <SchoolIcon color="primary" />,
            'orgIssues': <CorporateFareIcon color="primary" />,
            'existingTalentSkill': <WorkHistoryIcon color="primary" />,
            'pastRecruitData': <PreviewIcon color="primary" />,
            'employmentType': <WorkOutlineIcon color="primary" />,
            'workStyle': <WorkOutlineIcon color="primary" />,
            'prioritySchedule': <ScheduleIcon color="primary" />
          }[selectedField]}
          <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
            {selectedField && {
              'position': '募集職種',
              'targetHires': '希望採用人数',
              'budget': '採用予算',
              'startDate': '採用開始日',
              'endDate': '採用終了日',
              'desiredSkillLevel': '想定スキルレベル',
              'industry': '業界',
              'recruitType': '採用タイプ',
              'orgIssues': '組織の課題・採用背景',
              'existingTalentSkill': '既存人材スキル',
              'pastRecruitData': '過去の採用データ',
              'employmentType': '雇用形態',
              'workStyle': '働き方',
              'prioritySchedule': '採用優先スケジュール'
            }[selectedField]} を編集
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          {selectedField === 'recruitType' ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                ※ 新卒・中途いずれか、あるいは両方を選択してください
              </Typography>
              <FormGroup row sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      icon={<CheckCircleOutlineIcon />}
                      checkedIcon={<CheckCircleIcon />}
                      checked={localEditedRequirements.recruitType === 'newGrad' || localEditedRequirements.recruitType === 'both'}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const isMidCareerChecked = localEditedRequirements.recruitType === 'midCareer' || localEditedRequirements.recruitType === 'both';
                        
                        let newType = 'both';
                        if (isChecked && !isMidCareerChecked) newType = 'newGrad';
                        else if (!isChecked && isMidCareerChecked) newType = 'midCareer';
                        else if (!isChecked && !isMidCareerChecked) newType = '';
                        
                        // ローカルの状態は即時更新
                        setLocalEditedRequirements(prev => ({
                          ...prev,
                          recruitType: newType
                        }));
                      }}
                      name="newGradChecked"
                      sx={{
                        color: '#1E88E5',
                        '&.Mui-checked': {
                          color: '#1E88E5',
                        },
                      }}
                    />
                  }
                  label="新卒採用"
                  sx={{
                    '.MuiFormControlLabel-label': {
                      fontWeight: 'bold',
                      color: '#0D47A1',
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      icon={<CheckCircleOutlineIcon />}
                      checkedIcon={<CheckCircleIcon />}
                      checked={localEditedRequirements.recruitType === 'midCareer' || localEditedRequirements.recruitType === 'both'}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const isNewGradChecked = localEditedRequirements.recruitType === 'newGrad' || localEditedRequirements.recruitType === 'both';
                        
                        let newType = 'both';
                        if (isChecked && !isNewGradChecked) newType = 'midCareer';
                        else if (!isChecked && isNewGradChecked) newType = 'newGrad';
                        else if (!isChecked && !isNewGradChecked) newType = '';
                        
                        // ローカルの状態は即時更新
                        setLocalEditedRequirements(prev => ({
                          ...prev,
                          recruitType: newType
                        }));
                      }}
                      name="midCareerChecked"
                      sx={{
                        color: '#FB8C00',
                        '&.Mui-checked': {
                          color: '#FB8C00',
                        },
                      }}
                    />
                  }
                  label="中途採用"
                  sx={{
                    '.MuiFormControlLabel-label': {
                      fontWeight: 'bold',
                      color: '#E65100',
                    },
                  }}
                />
              </FormGroup>
              {(!localEditedRequirements.recruitType || localEditedRequirements.recruitType === '') && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1, fontWeight: 'medium' }}>
                  ※ 採用タイプを選択してください（必須項目）
                </Typography>
              )}
            </Box>
          ) : selectedField === 'startDate' || selectedField === 'endDate' ? (
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              label={selectedField === 'startDate' ? '採用開始日' : '採用終了日'}
              type="date"
              value={localEditedRequirements[selectedField] || ''}
              onChange={handleDialogTextChange}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              helperText={selectedField === 'endDate' ? '※ 採用開始日以降の日付を指定してください。' : null}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#4e7df3',
                  },
                },
              }}
            />
          ) : selectedField === 'prioritySchedule' ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                採用優先スケジュールを選択してください
              </Typography>
              <Grid container spacing={2}>
                {['Q1', 'Q2', 'Q3', 'Q4'].map((quarter, index) => (
                  <Grid item xs={3} key={quarter}>
                    <Box 
                      onClick={() => {
                        setLocalEditedRequirements(prev => ({
                          ...prev,
                          prioritySchedule: quarter
                        }));
                      }}
                      sx={{
                        border: '1px solid',
                        borderColor: localEditedRequirements.prioritySchedule === quarter ? '#4e7df3' : border(theme).default,
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: localEditedRequirements.prioritySchedule === quarter ? 'rgba(78, 125, 243, 0.08)' : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#4e7df3',
                          backgroundColor: 'rgba(78, 125, 243, 0.04)',
                        }
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          color: localEditedRequirements.prioritySchedule === quarter ? '#4e7df3' : 'text.secondary',
                        }}
                      >
                        {quarter}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: 'text.secondary',
                        }}
                      >
                        {['1-3月', '4-6月', '7-9月', '10-12月'][index]}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  または具体的な月を入力:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="例: 4月, 5月頃, 2024年1月"
                  value={!['Q1', 'Q2', 'Q3', 'Q4'].includes(localEditedRequirements.prioritySchedule) ? localEditedRequirements.prioritySchedule : ''}
                  onChange={handleDialogTextChange}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#4e7df3',
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          ) : selectedField === 'targetHires' || selectedField === 'budget' ? (
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              label={selectedField === 'targetHires' ? '希望人数' : '採用予算'}
              type="text"
              value={localEditedRequirements[selectedField] || ''}
              onChange={handleDialogTextChange}
              variant="outlined"
              helperText={selectedField === 'budget' ? '※ 半角数字のみ入力（円単位）' : '※ 半角数字のみ入力'}
              InputProps={{
                inputProps: { min: 1 }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#4e7df3',
                  },
                },
              }}
            />
          ) : (
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              value={localEditedRequirements[selectedField] || ''}
              onChange={handleDialogTextChange}
              variant="outlined"
              multiline={selectedField === 'orgIssues' || selectedField === 'existingTalentSkill' || selectedField === 'pastRecruitData'}
              rows={selectedField === 'orgIssues' || selectedField === 'existingTalentSkill' || selectedField === 'pastRecruitData' ? 4 : 1}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#4e7df3',
                  },
                },
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 2,
              borderColor: '#d0d5dd',
              color: '#606978',
              '&:hover': {
                borderColor: '#babeca',
                backgroundColor: bg(theme).subtle
              }
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleDialogConfirm}
            startIcon={<UpdateIcon />}
            variant="contained"
            color="primary"
            disabled={isRegenerating || isDialogInvalid()}
            sx={{
              borderRadius: 2,
              px: 3,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
              backgroundColor: '#4e7df3',
              fontWeight: 600,
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                backgroundColor: '#4065d0',
              },
              '&.Mui-disabled': {
                backgroundColor: border(theme).default,
                color: '#9e9e9e',
                boxShadow: 'none',
              }
            }}
          >
            更新して再生成
          </Button>
        </DialogActions>
      </Dialog>

      {/* バリデーションエラーメッセージ */}
      {isDialogInvalid() && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography 
            variant="caption" 
            color="error.main" 
            sx={{ 
              display: 'block', 
              textAlign: 'center',
              fontWeight: 'medium',
              p: 1,
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
              borderRadius: 1
            }}
          >
            {getDialogValidationErrorMessage()}
          </Typography>
        </Box>
      )}
    </Box>
  );
}